import React, { useEffect, useMemo, useRef, useState } from "react";
import { useGLTF, useTexture } from "@react-three/drei";
import { GroupProps, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import grassVert from "../shaders/grass-vert";
import grassFrag from "../shaders/grass-frag";
import scrollGrassVert from "../shaders/scroll-grass-vert";
import scrollGrassFrag from "../shaders/scroll-grass-frag";
import { assets } from "./Assets";
import useLoadTexture from "./UseLoadTexture";
import { Mesh, ShaderMaterial } from "three";
import useDarkMode from "../stores/useDarkMode";

type HerbProps = GroupProps & {
  model?: string;
  scrolling?: boolean;
  wind?: number;
}

export default function Grass(props: HerbProps) {

  const model = props.model || assets.grass.model;

  const { nodes } = useGLTF(model);

  const texture = useLoadTexture(assets.grass.textures.grass);
  const textureAlpha = useLoadTexture(assets.grass.textures.grassAlpha);

  const darkModeEnabled = useDarkMode(s => s.darkModeEnabled);

  const herbsShader = useRef<ShaderMaterial>(null!);

  const uniforms = useMemo(
    () => {
      let obj = THREE.UniformsLib.fog;
      obj = Object.assign(
        {
          uFrequency: { value: new THREE.Vector2(20, 5) },
          uTime: { value: 0 },
          uColor: { value: new THREE.Color("red") },
          uMap: { value: texture },
          uAlphaMap: { value: textureAlpha },
          uBoundingHeight: { value: 28 },
          uRange: { value: 7.75 },
          uSoftRange: { value: 0.25 },
          uLight: { value: 1 },
          uBranchBackdrop: { value: 0.4 },
          uWind: { value: props.wind || 15 }
        }, obj);
      return obj;
    },
    [texture, textureAlpha, props.wind]
  );

  useFrame(
    (state, delta) => {
      herbsShader.current.uniforms.uTime.value += delta;
      herbsShader.current.uniforms.uLight.value = darkModeEnabled ? 0.1 : 1;
    }
  );

  return (
    <group {...props} dispose={null}>
      <mesh
        geometry={(nodes.Grass as Mesh).geometry}
        material={(nodes.Grass as Mesh).material}
      >
        {
          props.scrolling
            ? <shaderMaterial
              fog
              ref={herbsShader}
              vertexShader={scrollGrassVert}
              fragmentShader={scrollGrassFrag}
              transparent
              side={THREE.DoubleSide}
              depthWrite={false}
              toneMapped={false}
              uniforms={uniforms}
            />
            : <shaderMaterial
              fog
              ref={herbsShader}
              vertexShader={grassVert}
              fragmentShader={grassFrag}
              transparent
              side={THREE.DoubleSide}
              depthWrite={false}
              toneMapped={false}
              uniforms={uniforms}
            />
        }
      </mesh>
    </group>
  );
}

useGLTF.preload("./assets/herbs/herb.glb");
